import { addToRecord, objectWithoutKeys } from '@ms/yammer-data-support';
import { PraiseBadge } from '@ms/yammer-graphql';

import {
  PublishersBeginReplyToDirectMessageAction,
  PublishersBeginReplyToGroupMessageAction,
  PublishersBeginReplyToNetworkQuestionMessageAction,
  PublishersBeginReplyToTeamsMeetingMessageAction,
  PublishersBeginReplyToUserMomentMessageAction,
  PublishersBeginReplyToUserStorylineMessageAction,
  PublishersOpenDirectNewAction,
  PublishersOpenDirectReplyAction,
  PublishersOpenEditMessageAction,
  PublishersOpenGroupNewPollAction,
  PublishersOpenGroupNewPraiseAction,
  PublishersOpenGroupNewQuestionAction,
  PublishersOpenGroupNewUpdateAction,
  PublishersOpenGroupReplyAction,
  PublishersOpenNetworkQuestionNewQuestionAction,
  PublishersOpenNetworkQuestionReplyAction,
  PublishersOpenShareNewDirectAction,
  PublishersOpenShareToNewUpdateAction,
  PublishersOpenTeamsMeetingReplyAction,
  PublishersOpenUserMomentNewAction,
  PublishersOpenUserMomentReplyAction,
  PublishersOpenUserNewPollAction,
  PublishersOpenUserNewPraiseAction,
  PublishersOpenUserNewQuestionAction,
  PublishersOpenUserNewUpdateAction,
  PublishersOpenUserReplyAction,
} from '../../domains/publishers/actions.types';
import { Publishers, Topic } from '../types';

import { openEventNewQuestionPublisher, openEventNewUpdatePublisher } from './eventPublisher';
import { PublishersAction } from './reducer.types';
import {
  openTeamsMeetingNewQuestionPublisher,
  openTeamsMeetingNewUpdatePublisher,
} from './teamsMeetingPublisherReducer';
import {
  DirectNewPublisher,
  DirectReplyPublisher,
  EditMessagePublisher,
  GroupNewPollPublisher,
  GroupNewPraisePublisher,
  GroupNewQuestionPublisher,
  GroupNewUpdatePublisher,
  GroupReplyPublisher,
  NetworkQuestionNewQuestionPublisher,
  NetworkQuestionReplyPublisher,
  Publisher,
  PublisherPostType,
  ShareAsDirectMessagePublisher,
  TeamsMeetingReplyPublisher,
  UserMomentNewPublisher,
  UserMomentReplyPublisher,
  UserNewPollPublisher,
  UserNewPraisePublisher,
  UserNewQuestionPublisher,
  UserNewUpdatePublisher,
  UserReplyPublisher,
} from './types';

const createPublisher = (id: Publisher['id'], defaultTopicId?: Topic['id']) => ({
  id,
  attachments: [],
  notifiedUserIds: [],
  hasAttemptedToAttachFile: false,
  isGifPickerOpen: false,
  isLinkModalOpen: false,
  isTopicBarOpen: false,
  postType: PublisherPostType.GroupNewUpdate,
  isExpanded: true,
  changedGroupWhileFilesAttached: false,
  changedPostOnBehalfOfUserWhileFilesAttached: false,
  topicNames: [],
  topicIds: defaultTopicId ? [defaultTopicId] : [],
  wasPublishedAsDraft: false,
});

const defaultPollOptions = ['', '', ''];
const createThreadStarterPublisher = (id: Publisher['id'], defaultTopicId?: Topic['id']) => ({
  ...createPublisher(id, defaultTopicId),
  badge: PraiseBadge.THUMBSUP,
  praisedUserIds: [],
  pollOptions: defaultPollOptions,
});

const openGroupNewUpdatePublisher = (
  state: Publishers,
  {
    id,
    groupId,
    sharedMessageId,
    publisherInstanceId,
    onBehalfOfSenderId,
    topicId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenGroupNewUpdateAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewUpdatePublisher = {
    ...createThreadStarterPublisher(id, topicId),
    publisherInstanceId,
    groupId,
    sharedMessageId,
    isAnnouncement: false,
    topicId,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.GroupNewUpdate,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    isTopicBarOpen: !!topicId,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openUserMomentNewPublisher = (
  state: Publishers,
  { id, userId, publisherInstanceId }: PublishersOpenUserMomentNewAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserMomentNewPublisher = {
    ...existingState,
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    postType: PublisherPostType.UserMomentNew,
    isExpanded: true,
    isGifPickerOpen: false,
    isAnnouncement: false,
    isLinkModalOpen: false,
    isMandatoryNotification: false,
    isTopicBarOpen: true,
    sharedThreadId: undefined,
    sharedMessageId: undefined,
  };

  return addToRecord(state, id, publisher);
};

const openUserMomentReplyPublisher = (
  state: Publishers,
  {
    id,
    userId,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    contentState,
  }: PublishersOpenUserMomentReplyAction['payload']
) => {
  const publisher: UserMomentReplyPublisher = {
    ...createPublisher(id),
    ...state[id],
    isExpanded: true,
    publisherInstanceId,
    userId,
    threadId,
    postType: PublisherPostType.UserMomentReply,
    isAnnouncement: false,
    isSecondLevelReply: false,
    contentState,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const openUserNewUpdatePublisher = (
  state: Publishers,
  {
    id,
    userId,
    sharedMessageId,
    publisherInstanceId,
    onBehalfOfSenderId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenUserNewUpdateAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewUpdatePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    sharedMessageId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.UserNewUpdate,
    isMandatoryNotification: false,
    onBehalfOfSenderId,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openUserNewQuestionPublisher = (
  state: Publishers,
  {
    id,
    publisherInstanceId,
    onBehalfOfSenderId,
    userId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenUserNewQuestionAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewQuestionPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.UserNewQuestion,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openUserNewPollPublisher = (
  state: Publishers,
  {
    id,
    publisherInstanceId,
    onBehalfOfSenderId,
    userId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenUserNewPollAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewPollPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    pollOptions: defaultPollOptions,
    isExpanded: true,
    postType: PublisherPostType.UserNewPoll,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openUserNewPraisePublisher = (
  state: Publishers,
  {
    id,
    publisherInstanceId,
    userId,
    campaignHashtag,
    campaignId,
    praisedUserIds = [],
    onBehalfOfSenderId,
  }: PublishersOpenUserNewPraiseAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: UserNewPraisePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    userId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.UserNewPraise,
    badge: PraiseBadge.THUMBSUP,
    praisedUserIds,
    isMandatoryNotification: false,
    campaignHashtag,
    campaignId,
    onBehalfOfSenderId,
  };

  return addToRecord(state, id, publisher);
};

const openShareNewDirectPublisher = (
  state: Publishers,
  { id, sharedMessageId, publisherInstanceId }: PublishersOpenShareNewDirectAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: ShareAsDirectMessagePublisher = {
    ...createPublisher(id),
    publisherInstanceId,
    sharedMessageId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.DirectNew,
    isTopicBarOpen: false,
    isMandatoryNotification: false,
  };

  return addToRecord(state, id, publisher);
};

const openGroupNewQuestionPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    topicId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenGroupNewQuestionAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewQuestionPublisher = {
    ...createThreadStarterPublisher(id, topicId),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    topicId,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.GroupNewQuestion,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    isTopicBarOpen: topicId != null,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openGroupNewPollPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenGroupNewPollAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewPollPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    ...existingState,
    pollOptions: defaultPollOptions,
    isExpanded: true,
    postType: PublisherPostType.GroupNewPoll,
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openGroupNewPraisePublisher = (
  state: Publishers,
  {
    id,
    groupId,
    publisherInstanceId,
    onBehalfOfSenderId,
    campaignHashtag,
    campaignId,
  }: PublishersOpenGroupNewPraiseAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewPraisePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    groupId,
    isAnnouncement: false,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.GroupNewPraise,
    badge: PraiseBadge.THUMBSUP,
    praisedUserIds: [],
    onBehalfOfSenderId,
    isMandatoryNotification: false,
    campaignHashtag,
    campaignId,
  };

  return addToRecord(state, id, publisher);
};

const openGroupReplyPublisher = (
  state: Publishers,
  {
    id,
    groupId,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    contentState,
    onBehalfOfSenderId,
  }: PublishersOpenGroupReplyAction['payload']
) => {
  const publisher: GroupReplyPublisher = {
    ...createPublisher(id),
    ...state[id],
    isExpanded: true,
    publisherInstanceId,
    groupId,
    threadId,
    postType: PublisherPostType.GroupReply,
    isAnnouncement: false,
    isSecondLevelReply: false,
    contentState,
    onBehalfOfSenderId,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const openDirectNewPublisher = (
  state: Publishers,
  { id, publisherInstanceId, contentState }: PublishersOpenDirectNewAction['payload']
) => {
  const publisher: DirectNewPublisher = {
    ...createPublisher(id),
    publisherInstanceId,
    postType: PublisherPostType.DirectNew,
    isAnnouncement: false,
    contentState,
    isTopicBarOpen: false,
    isMandatoryNotification: false,
  };

  return addToRecord(state, id, publisher);
};

const openUserReplyPublisher = (
  state: Publishers,
  { id, userId, threadId, emptyPublishers, publisherInstanceId, contentState }: PublishersOpenUserReplyAction['payload']
) => {
  const publisher: UserReplyPublisher = {
    ...createPublisher(id),
    ...state[id],
    isExpanded: true,
    publisherInstanceId,
    userId,
    threadId,
    postType: PublisherPostType.UserReply,
    isAnnouncement: false,
    isSecondLevelReply: false,
    contentState,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const openDirectReplyPublisher = (
  state: Publishers,
  { id, threadId, emptyPublishers, publisherInstanceId, contentState }: PublishersOpenDirectReplyAction['payload']
) => {
  const publisher: DirectReplyPublisher = {
    ...createPublisher(id),
    ...state[id],
    isExpanded: true,
    publisherInstanceId,
    threadId,
    postType: PublisherPostType.DirectReply,
    isAnnouncement: false,
    isSecondLevelReply: false,
    contentState,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const openNetworkQuestionReplyPublisher = (
  state: Publishers,
  {
    id,
    threadId,
    emptyPublishers,
    publisherInstanceId,
    contentState,
  }: PublishersOpenNetworkQuestionReplyAction['payload']
) => {
  const publisher: NetworkQuestionReplyPublisher = {
    ...createPublisher(id),
    ...state[id],
    isExpanded: true,
    publisherInstanceId,
    threadId,
    postType: PublisherPostType.NetworkQuestionReply,
    isAnnouncement: false,
    isSecondLevelReply: false,
    contentState,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const openTeamsMeetingReplyPublisher = (
  state: Publishers,
  { id, threadId, emptyPublishers, publisherInstanceId, contentState }: PublishersOpenTeamsMeetingReplyAction['payload']
) => {
  const publisher: TeamsMeetingReplyPublisher = {
    ...createPublisher(id),
    ...state[id],
    isExpanded: true,
    publisherInstanceId,
    threadId,
    postType: PublisherPostType.TeamsMeetingReply,
    isAnnouncement: false,
    isSecondLevelReply: false,
    contentState,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const openEditMessagePublisher = (
  state: Publishers,
  {
    id,
    messageId,
    notifiedUserIds,
    publisherInstanceId,
    praisedUserIds,
    badge,
    attachments,
    isExpanded,
    contentState,
    title,
    onBehalfOfSenderId,
  }: PublishersOpenEditMessageAction['payload']
) => {
  const publisher: EditMessagePublisher = {
    ...createPublisher(id),
    attachments,
    publisherInstanceId,
    messageId,
    notifiedUserIds,
    originalNotifiedUserIds: notifiedUserIds,
    postType: PublisherPostType.Edit,
    isAnnouncement: false,
    praisedUserIds,
    badge,
    isExpanded,
    contentState,
    title,
    isTopicBarOpen: false,
    isMandatoryNotification: false,
    hasRemovedAttachmentsNotDeletedFromSharePoint: false,
    onBehalfOfSenderId,
  };

  return addToRecord(state, id, publisher);
};

const openShareToNewUpdatePublisher = (
  state: Publishers,
  { id, publisherInstanceId }: PublishersOpenShareToNewUpdateAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: GroupNewUpdatePublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    isAnnouncement: false,
    groupId: null,
    ...existingState,
    isExpanded: true,
    attachments: [],
    postType: PublisherPostType.GroupNewUpdate,
    isMandatoryNotification: false,
  };

  return addToRecord(state, id, publisher);
};

const openNetworkQuestionNewQuestionPublisher = (
  state: Publishers,
  { id, publisherInstanceId, title }: PublishersOpenNetworkQuestionNewQuestionAction['payload']
) => {
  const existingState = state[id] ? state[id] : {};
  const publisher: NetworkQuestionNewQuestionPublisher = {
    ...createThreadStarterPublisher(id),
    publisherInstanceId,
    isAnnouncement: false,
    title,
    ...existingState,
    isExpanded: true,
    postType: PublisherPostType.NetworkQuestionNewQuestion,
    isMandatoryNotification: false,
    isTopicBarOpen: true,
    hasCollapsedRelatedQuestionsModule: false,
  };

  return addToRecord(state, id, publisher);
};

const beginReplyToNetworkQuestionMessage = (
  state: Publishers,
  {
    publisherId: id,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToNetworkQuestionMessageAction['payload']
): Publishers => {
  const publisher: NetworkQuestionReplyPublisher = {
    ...(state[id] || createPublisher(id)),
    publisherInstanceId,
    postType: PublisherPostType.NetworkQuestionReply,
    currentlyReplyingToId: messageId,
    threadId,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isExpanded: true,
    isAnnouncement: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const beginReplyToUserStorylineMessage = (
  state: Publishers,
  {
    publisherId: id,
    userId,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToUserStorylineMessageAction['payload']
): Publishers => {
  const publisher: UserReplyPublisher = {
    ...(state[id] || createPublisher(id)),
    publisherInstanceId,
    postType: PublisherPostType.UserReply,
    currentlyReplyingToId: messageId,
    userId,
    threadId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isExpanded: true,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const beginReplyToUserMomentMessage = (
  state: Publishers,
  {
    publisherId: id,
    userId,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToUserMomentMessageAction['payload']
): Publishers => {
  const publisher: UserMomentReplyPublisher = {
    ...(state[id] || createPublisher(id)),
    publisherInstanceId,
    postType: PublisherPostType.UserMomentReply,
    currentlyReplyingToId: messageId,
    userId,
    threadId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
    isExpanded: true,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const beginReplyToGroupMessage = (
  state: Publishers,
  {
    publisherId: id,
    groupId,
    publisherInstanceId,
    threadId,
    messageId,
    emptyPublishers,
    isSecondLevelReply,
    onBehalfOfSenderId,
  }: PublishersBeginReplyToGroupMessageAction['payload']
): Publishers => {
  const publisher: GroupReplyPublisher = {
    ...(state[id] || createPublisher(id)),
    publisherInstanceId,
    postType: PublisherPostType.GroupReply,
    currentlyReplyingToId: messageId,
    groupId,
    threadId,
    isAnnouncement: false,
    isSecondLevelReply,
    onBehalfOfSenderId,
    isTopicBarOpen: false,
    isExpanded: true,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const beginReplyToDirectMessage = (
  state: Publishers,
  {
    publisherId: id,
    threadId,
    messageId,
    publisherInstanceId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToDirectMessageAction['payload']
): Publishers => {
  const publisher: DirectReplyPublisher = {
    ...(state[id] || createPublisher(id)),
    postType: PublisherPostType.DirectReply,
    currentlyReplyingToId: messageId,
    threadId,
    publisherInstanceId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

const beginReplyToTeamsMeetingMessage = (
  state: Publishers,
  {
    publisherId: id,
    threadId,
    messageId,
    publisherInstanceId,
    emptyPublishers,
    isSecondLevelReply,
  }: PublishersBeginReplyToTeamsMeetingMessageAction['payload']
): Publishers => {
  const publisher: TeamsMeetingReplyPublisher = {
    ...(state[id] || createPublisher(id)),
    postType: PublisherPostType.TeamsMeetingReply,
    currentlyReplyingToId: messageId,
    threadId,
    publisherInstanceId,
    isAnnouncement: false,
    isSecondLevelReply,
    isTopicBarOpen: false,
  };
  const stateWithoutEmptyPublishers = objectWithoutKeys(state, emptyPublishers);

  return addToRecord(stateWithoutEmptyPublishers, id, publisher);
};

type OpenActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const openActionsReducer: OpenActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.OPEN_EVENT_NEW_UPDATE':
      return openEventNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_EVENT_NEW_QUESTION':
      return openEventNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_TEAMS_MEETING_NEW_UPDATE':
      return openTeamsMeetingNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_TEAMS_MEETING_NEW_QUESTION':
      return openTeamsMeetingNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_SHARE_TO_NEW_UPDATE':
      return openShareToNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_UPDATE':
      return openGroupNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_SHARE_NEW_DIRECT':
      return openShareNewDirectPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_QUESTION':
      return openGroupNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_POLL':
      return openGroupNewPollPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_NEW_PRAISE':
      return openGroupNewPraisePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_GROUP_REPLY':
      return openGroupReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_DIRECT_NEW':
      return openDirectNewPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_DIRECT_REPLY':
      return openDirectReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_NETWORK_QUESTION_REPLY':
      return openNetworkQuestionReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_TEAMS_MEETING_REPLY':
      return openTeamsMeetingReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_EDIT_MESSAGE':
      return openEditMessagePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_MOMENT_NEW':
      return openUserMomentNewPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_MOMENT_REPLY':
      return openUserMomentReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_UPDATE':
      return openUserNewUpdatePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_QUESTION':
      return openUserNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_POLL':
      return openUserNewPollPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_NEW_PRAISE':
      return openUserNewPraisePublisher(state, action.payload);
    case 'PUBLISHER.OPEN_USER_REPLY':
      return openUserReplyPublisher(state, action.payload);
    case 'PUBLISHER.OPEN_NETWORK_QUESTION_NEW_QUESTION':
      return openNetworkQuestionNewQuestionPublisher(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_USER_STORYLINE_MESSAGE':
      return beginReplyToUserStorylineMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_USER_MOMENT_MESSAGE':
      return beginReplyToUserMomentMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_GROUP_MESSAGE':
      return beginReplyToGroupMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_NETWORK_QUESTION_MESSAGE':
      return beginReplyToNetworkQuestionMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_DIRECT_MESSAGE':
      return beginReplyToDirectMessage(state, action.payload);
    case 'PUBLISHER.BEGIN_REPLY_TO_TEAMS_MEETING_MESSAGE':
      return beginReplyToTeamsMeetingMessage(state, action.payload);
    default:
      return state;
  }
};
